import { useState, useEffect, useCallback } from 'react';
import { clearApiKeyCache } from '../services/core/ApiKeyProvider';
import { keysApi } from '../services/keysApi';

async function storeKeyToBackend(provider: string, key: string): Promise<boolean> {
  try {
    await keysApi.addKey({ provider, key });
    return true;
  } catch {
    return false;
  }
}

async function loadKeysFromBackend(): Promise<Record<string, string> | null> {
  try {
    const entries = await keysApi.listKeys();
    const result: Record<string, string> = {};
    for (const entry of entries) {
      try {
        const useData = await keysApi.useKey(entry.provider);
        result[entry.provider] = useData.key;
      } catch {}
    }
    return result;
  } catch {
    return null;
  }
}

const KEY_PROVIDERS = [
  { provider: 'pollo', stateKey: 'polloKey', storageKey: 'pollo_api_key' },
  { provider: 'runninghub', stateKey: 'runningHubKey', storageKey: 'runninghub_api_key' },
  { provider: 'grsai', stateKey: 'grsaiKey', storageKey: 'grsai_api_key' },
  { provider: 'fal', stateKey: 'falKey', storageKey: 'fal_api_key' },
  { provider: 'agnes', stateKey: 'agnesKey', storageKey: 'agnes_api_key' },
] as const;

export function useApiKeyManager(isOpen: boolean) {
  const [polloKey, setPolloKey] = useState('');
  const [runningHubKey, setRunningHubKey] = useState('');
  const [grsaiKey, setGrsaiKey] = useState('');
  const [falKey, setFalKey] = useState('');
  const [agnesKey, setAgnesKey] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  const keySetters: Record<string, (v: string) => void> = {
    polloKey: setPolloKey,
    runningHubKey: setRunningHubKey,
    grsaiKey: setGrsaiKey,
    falKey: setFalKey,
    agnesKey: setAgnesKey,
  };

  const keyGetters: Record<string, string> = {
    polloKey: polloKey,
    runningHubKey: runningHubKey,
    grsaiKey: grsaiKey,
    falKey: falKey,
    agnesKey: agnesKey,
  };

  useEffect(() => {
    const loadKeys = async () => {
      const backendKeys = await loadKeysFromBackend();
      if (backendKeys) {
        if (backendKeys.pollo) setPolloKey(backendKeys.pollo);
        if (backendKeys.runninghub) setRunningHubKey(backendKeys.runninghub);
        if (backendKeys.grsai) setGrsaiKey(backendKeys.grsai);
        if (backendKeys.fal) setFalKey(backendKeys.fal);
        if (backendKeys.agnes) setAgnesKey(backendKeys.agnes);
        return;
      }
      for (const { storageKey, stateKey } of KEY_PROVIDERS) {
        const stored = localStorage.getItem(storageKey);
        if (stored) keySetters[stateKey](stored);
      }
    };
    if (isOpen) loadKeys();
  }, [isOpen]);

  const handleSave = useCallback(async () => {
    const keysToSave = [
      { provider: 'pollo', key: polloKey.trim(), storageKey: 'pollo_api_key' },
      { provider: 'runninghub', key: runningHubKey.trim(), storageKey: 'runninghub_api_key' },
      { provider: 'grsai', key: grsaiKey.trim(), storageKey: 'grsai_api_key' },
      { provider: 'fal', key: falKey.trim(), storageKey: 'fal_api_key' },
      { provider: 'agnes', key: agnesKey.trim(), storageKey: 'agnes_api_key' },
    ];
    // [FIX] 修复：移除 line 93-96 的无条件写入，保留条件逻辑
    for (const { provider, key, storageKey } of keysToSave) {
      if (key) {
        const backendOk = await storeKeyToBackend(provider, key);
        if (!backendOk) {
          localStorage.setItem(storageKey, key);
        }
      } else {
        localStorage.removeItem(storageKey);
      }
    }
    clearApiKeyCache();
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  }, [polloKey, runningHubKey, grsaiKey, falKey, agnesKey]);

  return {
    polloKey, setPolloKey,
    runningHubKey, setRunningHubKey,
    grsaiKey, setGrsaiKey,
    falKey, setFalKey,
    agnesKey, setAgnesKey,
    isSaved,
    handleSave,
  };
}
